import { ErrorBoundaryFallback } from "@app/components/error-boundary-fallback.tsx";
import Footer from "@app/components/layout/footer.tsx";
import PageTitle from "@app/components/page-title.tsx";
import useAuth from "@app/hooks/use-auth.ts";
import useNavItems from "@app/hooks/use-nav-items.tsx";
import { themeModeStore } from "@app/stores/theme.store.ts";
import { Box, Typography, useTheme } from "@mui/material";
import { createRootRouteWithContext, Outlet, useLocation } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useStore } from "@tanstack/react-store";
import { Navbar } from "bi-lib";
import { useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

interface RouterContext {
    isAuthenticated: boolean;
    isAdmin: boolean;
    isManager: boolean;
}

export const Route = createRootRouteWithContext<RouterContext>()({
    component: Root,
    notFoundComponent: NotFound,
});

function Root() {
    const [headerHeight, setHeaderHeight] = useState(0);

    const themeMode = useStore(themeModeStore);

    const { mainNav, userNav } = useNavItems();
    const { isAuthenticated, userData } = useAuth();
    const theme = useTheme();

    const headerRef = useRef<HTMLDivElement>(null);

    const { pathname } = useLocation();

    useEffect(() => {
        if (headerRef.current == undefined) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            setHeaderHeight(headerRef.current?.clientHeight ?? 0);
        });
        resizeObserver.observe(headerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    return (
        <Box
            display="flex"
            flexDirection="column"
            minHeight={"100vh"}
            sx={{ backgroundColor: theme.palette.background.default }}
        >
            <Box position={"fixed"} width={"100%"} ref={headerRef} zIndex={1000}>
                <Navbar
                    logo={
                        <Typography fontWeight={"bolder"} color={"white"} variant={"h5"} marginRight={2}>
                            TEIRS
                        </Typography>
                    }
                    navItems={mainNav}
                    isAuthenticated={isAuthenticated}
                    userNavItems={userNav}
                    userAvatarBackground={"linear-gradient(45deg, #a00 30%, #d00 90%)"}
                    userName={userData?.UserName}
                    themeMode={themeMode}
                    onThemeModeChange={(themeMode) => themeModeStore.setState(() => themeMode)}
                />
            </Box>
            <Box p={2} flexGrow={1} mt={`${headerHeight}px`}>
                <ErrorBoundary
                    FallbackComponent={ErrorBoundaryFallback}
                    onReset={() => window.location.reload()}
                    key={pathname}
                >
                    <Outlet />
                </ErrorBoundary>
            </Box>
            <Footer />
            {process.env.NODE_ENV == "development" && <TanStackRouterDevtools />}
        </Box>
    );
}

function NotFound() {
    return (
        <>
            <PageTitle>Not Found</PageTitle>
            <Typography variant={"body1"}>The requested page could not be found</Typography>{" "}
        </>
    );
}
